import React, { Component } from 'react';
import { Container, Row, Col, Form } from "react-bootstrap";
import { Button, FormControlLabel, Radio, RadioGroup, Checkbox } from "@mui/material";
import axios from "axios";
import Swal from "sweetalert2";
import { round2 } from '../../helper';
import ByShop from './ByShop';
import ByAccount from './ByAccount';
import PageHeader from '../../Pages/admin/shared/PageHeader';
import PrintButton from '../../Pages/admin/shared/PrintButton';
import FilterCard from '../../Pages/admin/shared/FilterCard';

/* SHOP PARTNERSHIP VIEWER (added 2026-10-20)
 *
 * Answers "which accounts are on this shop, and for what percentage" straight from the Pay Account
 * setup. It reports CONFIGURATION, not money — no week range, no ledger — which is why it is an
 * as-at date rather than a From/To week like the finance reports.
 *
 * WHY AN AS-AT DATE. A shop accumulates consecutive windows for the same account at different
 * rates, so "every window ever" sums past 100% and looks broken: Bitterne Road reads 204.14% over
 * all time but 58.32% on any single day. Defaulting to today means the total always reconciles
 * with the list beneath it. "Show all windows ever" is there when the history is the question, and
 * it says so in the heading so the big totals are not mistaken for an error.
 *
 * The grouping is done here rather than in SQL because the endpoint returns one flat row set and
 * both views need the same rows shaped differently — doing it twice in SQL is how two views drift
 * apart.
 */
class ShopPartnership extends Component {
    constructor(props) {
        super(props);
        this.state = {
            // Default to today: the live split, which is what people ask for.
            AsAtDate: new Date().toISOString().slice(0, 10),
            AllWindows: false,
            reportView: 'by_shop',
            showReport: false,
            ReportDetails: { byShop: [], byAccount: [], asAtLabel: '' },
        };
    }

    handleChange(field, evt) {
        const value = (field === 'AllWindows') ? evt.target.checked : evt.target.value;
        this.setState({ [field]: value });
    }

    async viewReport() {
        const { AsAtDate, AllWindows } = this.state;
        if (!AllWindows && !AsAtDate) {
            Swal.fire('Error', 'Pick an as-at date, or tick "show all windows ever".', 'error');
            return;
        }

        this.props.toggleLoader(true);
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BASE_URL}services/report/shopPartnership.php`,
                { AsAtDate: AllWindows ? '' : AsAtDate }
            );
            const d = res.data;
            if (!d || d.success !== 1) {
                Swal.fire('Error', (d && d.message) || 'Could not load the report.', 'error');
                this.props.toggleLoader(false);
                return;
            }

            const partners = d.partners || [];
            const cards = d.cards || [];
            const shops = d.shops || [];

            /* ── BY SHOP ──────────────────────────────────────────────────────────────────
               Keyed on shop id as a STRING on both sides: pc_shop.PC_SP_ID is an int column
               while every other shop id in this database is varchar, and a strict compare
               between the two silently matches nothing. */
            const byShop = shops.map(s => {
                const key = String(s.ShopID);
                const sp = partners.filter(p => String(p.ShopID) === key);
                const sc = cards.filter(c => String(c.ShopID) === key);
                const partnerIds = sp.map(p => String(p.AccountID));
                return {
                    ShopID: s.ShopID,
                    ShopName: s.ShopName,
                    partners: sp,
                    cards: sc,
                    // Sum the parsed numbers, not the strings: PS_ACC_PRC is varchar.
                    totalPct: round2(sp.reduce((t, p) => t + (+p.Pct || 0), 0)),
                    orphanCount: sp.filter(p => +p.IsOrphan).length
                        + sc.filter(c => +c.IsOrphan).length,
                    // Accounts holding BOTH here, so the card row can say so instead of
                    // reading as a second, separate person.
                    bothIds: sc.map(c => String(c.AccountID)).filter(id => partnerIds.includes(id)),
                };
            })
                // Shops with something first; the rest still listed, so "no partners" is
                // visibly different from "filtered out".
                .sort((a, b) =>
                    (b.partners.length + b.cards.length) - (a.partners.length + a.cards.length)
                    || String(a.ShopName).localeCompare(String(b.ShopName)));

            /* ── BY ACCOUNT ───────────────────────────────────────────────────────────── */
            const accMap = new Map();
            const put = (row, kind) => {
                const id = String(row.AccountID);
                if (!accMap.has(id)) {
                    accMap.set(id, {
                        AccountID: row.AccountID, AccountTitle: row.AccountTitle,
                        isOrphan: !!+row.IsOrphan, links: [],
                    });
                }
                accMap.get(id).links.push({ ...row, kind });
            };
            partners.forEach(p => put(p, 'p'));
            cards.forEach(c => put(c, 'c'));

            const byAccount = Array.from(accMap.values()).map(a => ({
                ...a,
                partnerShops: new Set(a.links.filter(l => l.kind === 'p').map(l => String(l.ShopID))).size,
                cardShops: new Set(a.links.filter(l => l.kind === 'c').map(l => String(l.ShopID))).size,
                links: a.links.sort((x, y) =>
                    String(x.ShopName).localeCompare(String(y.ShopName)) || x.kind.localeCompare(y.kind)),
            })).sort((a, b) => b.partnerShops - a.partnerShops
                || String(a.AccountTitle).localeCompare(String(b.AccountTitle)));

            const asAtLabel = d.AllWindows
                ? 'All windows ever recorded — totals above 100% are consecutive windows stacking, not an error'
                : `As at ${d.AsAtDate}`;

            this.setState({ showReport: true, ReportDetails: { byShop, byAccount, asAtLabel } });
        } catch (e) {
            console.error('shop partnership report :>> ', e);
            Swal.fire('Error', 'Could not load the report.', 'error');
        }
        this.props.toggleLoader(false);
    }

    render() {
        const { reportView, showReport, ReportDetails, AllWindows } = this.state;

        return (
            <Container fluid>
                <PageHeader breadcrumb="section" title="Shop Partnership Viewer"
                    right={<PrintButton content={() => this.newRef} disabled={this.state.showReport !== true} />} />

                <FilterCard>
                    <Row style={{ alignItems: 'center' }}>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>As at date</Form.Label>
                                <Form.Control
                                    type="date"
                                    disabled={AllWindows}
                                    value={this.state.AsAtDate}
                                    onChange={this.handleChange.bind(this, 'AsAtDate')}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <FormControlLabel
                                control={<Checkbox checked={AllWindows}
                                    onChange={this.handleChange.bind(this, 'AllWindows')} />}
                                label="Show all windows ever"
                            />
                        </Col>
                        <Col md={3}>
                            <RadioGroup row name="reportView" value={reportView}
                                onChange={this.handleChange.bind(this, 'reportView')}>
                                <FormControlLabel value="by_shop" control={<Radio />} label="By Shop" />
                                <FormControlLabel value="by_account" control={<Radio />} label="By Account" />
                            </RadioGroup>
                        </Col>
                        <Col style={{ alignSelf: "center" }}>
                            <Button onClick={this.viewReport.bind(this)} variant="contained" size='small'>View</Button>
                        </Col>
                    </Row>
                </FilterCard>

                {showReport === true
                    ? (reportView === 'by_account'
                        ? <ByAccount ref={el => (this.newRef = el)}
                            rows={ReportDetails.byAccount} asAtLabel={ReportDetails.asAtLabel} />
                        : <ByShop ref={el => (this.newRef = el)}
                            rows={ReportDetails.byShop} asAtLabel={ReportDetails.asAtLabel} />)
                    : ""}
            </Container>
        );
    }
}

export default ShopPartnership;
